import React, {
    ReactNode,
    useEffect, useMemo, useState,
    JSX,
} from "react";

import classNames from "classnames";
import { OperationVariables, QueryHookOptions, QueryResult } from "@apollo/client";
import useChangingCallback from "../../../use-changing-callback";
import {
    DefaultObject, FormTracker, FormTrackerBuilder, useFormTracker,
} from "../../dirty-tracker/FormTracker";
import { LoadingIcon } from "../../loading/Loading";
import Portal from "../../portal/Portal";
import Button from "../button/Button";
import Styles from "./Drawer.module.scss";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";

type AsProp = keyof JSX.IntrinsicElements;

export type Size = "xsmall" | "small" | "medium" | "large" | "xlarge";

export interface DrawerProps {
    title?: ReactNode;
    onHide: () => void;
    children: React.ReactNode;
    shouldShow: boolean;
    placement?: "start" | "end";
    onSave?: () => (void | Promise<void>);
    saveButtonText?: string;
    cancelButtonText?: string;

    size?: Size;
    isLoading?: boolean;
    as?: AsProp;
    className?: string;
    bodyClassName?: string;
}
export default function Drawer({
    shouldShow,
    title,
    onHide,
    children,
    onSave,
    saveButtonText,
    cancelButtonText,
    size,
    placement,
    isLoading,
    as: asType,
    className,
    bodyClassName,
}: DrawerProps) {
    const [showClass, setShowClass] = useState("none");

    const hide = useChangingCallback(() => {
        if (!shouldShow) {
            setShowClass("none");
        }
    }, [shouldShow]);
    useEffect(() => {
        if (shouldShow) {
            setShowClass("");
            setTimeout(() => {
                setShowClass("show");
            }, 1);
        } else {
            setShowClass("hiding");
            setTimeout(() => {
                if (!shouldShow) {
                    hide();
                }
            }, 1000);
        }
    }, [shouldShow]);

    const ParentComponent = asType ?? "div";

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        LoggingHandler.instance.wrap(async () => {
            await onSave?.();
        });
    };
    return showClass !== "none" ? (
        <Portal>
            {shouldShow && (
                <div
                    className={Styles.backdrop}
                    onKeyDown={onHide}
                    onClick={onHide}
                    role="presentation"
                />
            )}

            <ParentComponent
                className={classNames(
                    Styles.drawer,
                    "offcanvas",
                    `offcanvas-${placement}`,
                    showClass,
                    Styles[`drawer-${size}`],
                    className,
                )}
                onSubmit={submit}
            >
                <div className={Styles.header}>
                    {title && (
                        <div className={Styles.title}>{title}</div>
                    )}
                    <Button variant="ghost-gray" size="small" isClose onClick={onHide} className={Styles.close} />
                </div>
                {isLoading ? (
                    <div className={Styles.body}>
                        <div className={Styles["loading-container"]}>
                            <LoadingIcon />
                        </div>
                    </div>
                ) : (
                    <div className={Styles.body}>
                        <div className={classNames(Styles["body-container"], "drawer-body-container", bodyClassName)}>
                            {children}
                        </div>
                        {onSave && (
                            <div className={Styles.footer}>
                                <Button size="medium" onClick={onHide} variant="outline-gray">{cancelButtonText}</Button>
                                <Button
                                    size="medium"
                                    type="submit"
                                    variant="primary"
                                >
                                    {saveButtonText}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

            </ParentComponent>
        </Portal>
    ) : undefined;
}
Drawer.defaultProps = {
    title: undefined,
    onSave: undefined,
    saveButtonText: "Save",
    cancelButtonText: "Cancel",
    size: "small",
    placement: "end",
    isLoading: false,
    as: "div",
    className: "",
    bodyClassName: "",
};

interface UseDrawerProps {
    title: ReactNode;
    onSave?: () => (void | Promise<void>);
    onHide?: () => void;
    body?: React.ReactNode;
    size?: Size;
    isLoading?: boolean;
    className?: string
    saveButtonText?: string;
    cancelButtonText?: string
    bodyClassName?: string;
    placement?: "start" | "end";
}

export function useDrawer({
    title,
    onSave,
    onHide,
    body,
    size,
    isLoading,
    className,
    saveButtonText,
    cancelButtonText,
    bodyClassName,
}: UseDrawerProps) {
    const [shouldShow, setShouldShow] = useState(false);

    const element = useMemo(() => (
        <Drawer
            onSave={onSave ? () => {
                onSave?.();
                setShouldShow(false);
            } : undefined}
            shouldShow={shouldShow}
            onHide={() => {
                setShouldShow(false);
                onHide?.();
            }}
            title={title}
            size={size}
            isLoading={isLoading}
            as="form"
            className={className}
            saveButtonText={saveButtonText}
            cancelButtonText={cancelButtonText}
            bodyClassName={bodyClassName}
        >
            {body}
        </Drawer>
    ), [shouldShow, title, body, onSave, size]);

    return {
        element,
        setOpen: setShouldShow,
        isShowing: shouldShow,
    };
}

interface FormTrackerBuilderProperties<T extends DefaultObject> {
    handler: (builder: FormTrackerBuilder<unknown>) => FormTrackerBuilder<T>,
    dependencies: React.DependencyList,
}

function FormGuard<T extends DefaultObject, TValue>({
    formTracker,
    body,
    properties,
}: {
    formTracker: FormTracker<T>;
    body: (formTracker: FormTracker<T>) => React.ReactNode;
    properties: FormTrackerBuilderProperties<T>
}) {
    formTracker.useBuilder<T, TValue>(properties.handler, properties.dependencies);
    return body(formTracker);
}

export function useDrawerForm<T extends DefaultObject = never>({
    title,
    onSave,
    onHide,
    body,
    size,
    isLoading,
    formTrackerBuilder,
    className,
    bodyClassName,
}: {
    title: string;
    className?: string;
    onSave?: () => (void | Promise<void>);
    onHide?: () => void;
    body: (tracker: (T extends never ? never : FormTracker<T>)) => React.ReactNode;
    size?: Size;
    isLoading?: boolean;
    formTrackerBuilder?: FormTrackerBuilderProperties<T>
    bodyClassName?: string;
}) {
    const tracker = useFormTracker<T>();

    let bodyContent;
    if (formTrackerBuilder) {
        bodyContent = (
            <FormGuard
                body={body as ((arg: FormTracker<T>) => ReactNode)}
                formTracker={tracker}
                properties={formTrackerBuilder}
            />
        );
    } else {
        bodyContent = body(tracker as never);
    }

    const drawer = useDrawer({
        title,
        onSave,
        size,
        isLoading,
        onHide,
        className: classNames(Styles.form, className),
        body: bodyContent,
        bodyClassName,
    });

    return {
        element: drawer.element,
        setOpen: drawer.setOpen,
        formTracker: tracker,
    };
}

type OperationVariablesWithSkip = OperationVariables;
interface UseFormLoadedDrawerProps<
    TData,
    TVariables extends OperationVariablesWithSkip = OperationVariablesWithSkip
> extends UseDrawerProps {
    query: (options: QueryHookOptions<TData, TVariables> & {skip: boolean}) => QueryResult<TData, TVariables>;
    queryOptions: QueryHookOptions<TData, TVariables>;
    bodyCreator: (data: QueryResult<TData, TVariables>) => React.ReactNode;
    dependencies?: React.DependencyList;
    shouldHandleLoading?: boolean
}

export function useFormLoadedDrawer<TData, TVariables extends OperationVariablesWithSkip = OperationVariablesWithSkip>(
    props: UseFormLoadedDrawerProps<TData, TVariables>,
) {
    const [shouldShow, setShouldShow] = useState(false);
    const query = props.query({
        ...props.queryOptions,
        skip: props.queryOptions.skip || !shouldShow,
    });

    const element = useMemo(() => (
        <Drawer
            placement={props.placement}
            onSave={props.onSave ? () => {
                props.onSave?.();
                setShouldShow(false);
            } : undefined}
            shouldShow={shouldShow}
            onHide={() => {
                setShouldShow(false);
                props.onHide?.();
            }}
            isLoading={query.loading && props.shouldHandleLoading !== true}
            as="form"
            {...props}
        >
            {props.bodyCreator != null && shouldShow && (query.data || props.shouldHandleLoading)
                ? props.bodyCreator(query)
                : null}
        </Drawer>
    ), [shouldShow, props.title, props.body, props.onSave, props.size, query, ...(props.dependencies ?? [])]);

    return {
        element,
        setOpen: setShouldShow,
        isShowing: shouldShow,
    };
}
